<template>
  <footer class="bg-white border-t">
    <div class="container-custom py-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
        <!-- Company Info -->
        <div>
          <h3 class="font-semibold text-gray-900 mb-4">
            {{ companyStore.company?.name || 'Delivery SaaS' }}
          </h3>
          <p class="text-gray-600 text-sm mb-4">
            {{ companyStore.company?.description || 'Peça seus favoritos online' }}
          </p>
          <div class="space-y-2 text-sm text-gray-600">
            <p v-if="companyStore.company?.phone">
              <PhoneIcon class="inline h-4 w-4 mr-2" />
              {{ companyStore.company.phone }}
            </p>
            <p v-if="companyStore.company?.address">
              <MapPinIcon class="inline h-4 w-4 mr-2" />
              {{ companyStore.company.address }}
            </p>
            <p v-if="companyStore.company?.email">
              <EnvelopeIcon class="inline h-4 w-4 mr-2" />
              {{ companyStore.company.email }}
            </p>
          </div>
        </div>
        
        <!-- Quick Links -->
        <div>
          <h3 class="font-semibold text-gray-900 mb-4">
            Menu
          </h3>
          <ul class="space-y-2">
            <li>
              <router-link to="/" class="text-gray-600 hover:text-gray-900 text-sm">
                Início
              </router-link>
            </li>
            <li>
              <router-link to="/menu" class="text-gray-600 hover:text-gray-900 text-sm">
                Cardápio
              </router-link>
            </li>
            <li>
              <router-link to="/cart" class="text-gray-600 hover:text-gray-900 text-sm">
                Carrinho
              </router-link>
            </li>
            <li>
              <router-link to="/order/track" class="text-gray-600 hover:text-gray-900 text-sm">
                Meus Pedidos
              </router-link>
            </li>
          </ul>
        </div>
        
        <!-- Social Media -->
        <div>
          <h3 class="font-semibold text-gray-900 mb-4">
            Redes Sociais
          </h3>
          <div class="flex flex-wrap gap-3">
            <a
              v-if="companyStore.company?.facebook_url"
              :href="companyStore.company.facebook_url"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
              :style="{ backgroundColor: companyStore.primaryColor + '15', color: companyStore.primaryColor }"
              title="Facebook"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              v-if="companyStore.company?.instagram_url"
              :href="companyStore.company.instagram_url"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
              :style="{ backgroundColor: companyStore.primaryColor + '15', color: companyStore.primaryColor }"
              title="Instagram"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a
              v-if="companyStore.company?.twitter_url"
              :href="companyStore.company.twitter_url"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
              :style="{ backgroundColor: companyStore.primaryColor + '15', color: companyStore.primaryColor }"
              title="X (Twitter)"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a
              v-if="companyStore.company?.whatsapp"
              :href="`https://wa.me/${companyStore.company.whatsapp.replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
              :style="{ backgroundColor: companyStore.primaryColor + '15', color: companyStore.primaryColor }"
              title="WhatsApp"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </div>
          <p v-if="!hasSocialLinks" class="text-sm text-gray-400 mt-2">
            Nenhuma rede social configurada
          </p>
        </div>
        
        <!-- Opening Hours -->
        <div v-if="companyStore.company?.opening_hours">
          <h3 class="font-semibold text-gray-900 mb-4">
            Horário de Funcionamento
          </h3>
          <ul class="space-y-1 text-sm text-gray-600">
            <li v-for="(hours, day) in companyStore.company.opening_hours" :key="day">
              <span class="capitalize">{{ day }}:</span>
              <span v-if="hours.open && hours.close">
                {{ hours.open }} - {{ hours.close }}
              </span>
              <span v-else class="text-red-500">
                Fechado
              </span>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- Bottom Bar -->
      <div class="mt-8 pt-8 border-t text-center text-sm text-gray-500">
        <p>&copy; {{ new Date().getFullYear() }} {{ companyStore.company?.name || 'Delivery SaaS' }} - OBSistemas. Todos os direitos reservados.</p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/vue/24/outline'
import { useCompanyStore } from '@/stores'

const companyStore = useCompanyStore()

const hasSocialLinks = computed(() => {
  const c = companyStore.company
  return c && (c.facebook_url || c.instagram_url || c.twitter_url || c.whatsapp)
})
</script>
